import {combine, merge, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {
    data$,
    defaultFilters,
    defaultSortings,
    dictionary$,
    fetchDataFx,
    fetchDictionaryFx,
    filters$,
    filtersChanged,
    filtersReset,
    Gate,
    gridTokensCache$,
    pageNumber$,
    pageSize$,
    search$,
    searchChanged,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    tableStateChanged,
} from './index';

data$.on(fetchDataFx.doneData, (_, result) => result).reset(Gate.close);
dictionary$.on(fetchDictionaryFx.doneData, (_, result) => result).reset(Gate.close);
pageNumber$.reset(filtersChanged, sortingsChanged, stateReset, searchChanged);
pageSize$.reset(stateReset);

gridTokensCache$
    .on(fetchDataFx.done, (state, {params, result}) => ({
        ...state,
        [params.paging.pageNumber + 1]: result.nextPageRef ?? null,
    }))
    .reset(stateReset, Gate.close, filtersChanged, sortingsChanged, searchChanged);

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

const gridFetchSource = combine({
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    search: search$,
    cache: gridTokensCache$,
});

const mapGridFetchParams = ({
    pageNumber,
    pageSize,
    filters,
    sortings,
    search,
    cache,
}: {
    pageNumber: number;
    pageSize: number;
    filters: Parameters<typeof mapFilters>[0];
    sortings: Parameters<typeof mapSortings>[0];
    search: string;
    cache: Record<number, string | null>;
}) => ({
    paging: {pageNumber, pageSize},
    filters: mapFilters(filters),
    sortings: mapSortings(sortings),
    search,
    pageRef: cache[pageNumber] ?? null,
});

sample({
    clock: Gate.open,
    source: gridFetchSource,
    fn: mapGridFetchParams,
    target: fetchDictionaryFx,
});

sample({
    clock: merge([Gate.open, tableStateChanged, searchChanged]),
    source: gridFetchSource,
    fn: mapGridFetchParams,
    target: fetchDataFx,
});

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
fetchDictionaryFx.fail.watch(({error}) => showRestError(error as RestError));
